import React, { useState, useEffect } from 'react'
import { 
  X, 
  Palette, 
  Wrench, 
  Camera, 
  Beaker, 
  Hammer, 
  Car, 
  Utensils, 
  Zap, 
  Music, 
  Mountain, 
  Scissors, 
  Cpu,
  Package,
  FolderOpen,
  Clock,
  DollarSign,
  Users,
  Star,
  CheckCircle,
  ArrowRight
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { templatesApi } from '@/services/api'

interface BusinessTemplate {
  id: string
  name: string
  businessType: string
  description: string
  industry: string
  complexity: 'beginner' | 'intermediate' | 'advanced'
  tags: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface TemplatePreview {
  categories: Array<{
    id: string
    name: string
    description: string
    unitTypes: string[]
  }>
  projectTypes: Array<{
    id: string
    name: string
    description: string
    complexity: string
  }>
  activityTypes: Array<{
    id: string
    name: string
    description: string
    hourlyRate: number
  }>
  unitTypes: Array<{
    id: string
    name: string
    symbol: string
    category: string
  }>
  financialConfig: {
    defaultMarkup: number
    laborRate: number
    overheadRate: number
    profitMargin: number
    seasonalFactors: string[]
    pricingStrategies: string[]
    customerSegments: string[]
  }
  stats: {
    usageCount: number
    averageRating: number
    lastUpdated: string
  }
}

interface TemplatePreviewModalProps {
  template: BusinessTemplate | null
  isOpen: boolean
  onClose: () => void
  onSelect: (template: BusinessTemplate) => void
  currentTemplateId?: string
  mode?: 'onboarding' | 'switch'
}

// Industry icons mapping
const industryIcons: Record<string, React.ComponentType<any>> = {
  fashion: Palette,
  crafts: Hammer,
  technology: Cpu,
  food: Utensils,
  automotive: Car,
  arts: Camera,
  music: Music,
  outdoor: Mountain,
  textiles: Scissors,
  electronics: Zap,
  science: Beaker,
  tools: Wrench,
}

// Complexity colors
const complexityColors = {
  beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  advanced: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  isOpen,
  onClose,
  onSelect,
  currentTemplateId,
  mode = 'onboarding'
}) => {
  const [preview, setPreview] = useState<TemplatePreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'categories' | 'projects' | 'activities' | 'financial'>('overview')

  useEffect(() => {
    if (template && isOpen) {
      fetchPreview()
    }
  }, [template, isOpen])

  const fetchPreview = async () => {
    if (!template) return

    try {
      setLoading(true)
      const [previewData, stats] = await Promise.all([
        templatesApi.getPreview(template.id),
        templatesApi.getStats(template.id)
      ])
      
      setPreview({
        ...previewData,
        stats
      })
    } catch (error) {
      console.error('Error fetching template preview:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTemplateIcon = (template: BusinessTemplate) => {
    const IconComponent = industryIcons[template.industry] || Hammer
    return <IconComponent className="w-6 h-6" />
  }

  const handleSelect = () => {
    if (template) {
      onSelect(template)
    }
  }

  if (!isOpen || !template) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
              {getTemplateIcon(template)}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {template.name}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 capitalize">
                {template.industry} • {template.businessType}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {currentTemplateId === template.id && (
              <Badge variant="success" size="sm">
                <CheckCircle className="w-3 h-3 mr-1" />
                Current
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-200px)]">
          {/* Sidebar Navigation */}
          <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4">
            <nav className="space-y-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4" />
                  <span>Overview</span>
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('categories')}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  activeTab === 'categories'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FolderOpen className="w-4 h-4" />
                  <span>Categories</span>
                  {preview && (
                    <Badge variant="secondary" size="xs">
                      {preview.categories.length}
                    </Badge>
                  )}
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('projects')}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  activeTab === 'projects'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Wrench className="w-4 h-4" />
                  <span>Project Types</span>
                  {preview && (
                    <Badge variant="secondary" size="xs">
                      {preview.projectTypes.length}
                    </Badge>
                  )}
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('activities')}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  activeTab === 'activities'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>Activities</span>
                  {preview && (
                    <Badge variant="secondary" size="xs">
                      {preview.activityTypes.length}
                    </Badge>
                  )}
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('financial')}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  activeTab === 'financial'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4" />
                  <span>Financial</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Description */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Description
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {template.description}
                      </p>
                    </div>

                    {/* Template Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="p-4">
                        <div className="text-center">
                          <Badge className={complexityColors[template.complexity]} size="sm">
                            {template.complexity}
                          </Badge>
                          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            Complexity Level
                          </div>
                        </div>
                      </Card>
                      
                      {preview && (
                        <>
                          <Card className="p-4">
                            <div className="text-center">
                              <div className="flex items-center justify-center space-x-1">
                                <Users className="w-4 h-4 text-gray-500" />
                                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {preview.stats.usageCount}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                Active Users
                              </div>
                            </div>
                          </Card>
                          
                          <Card className="p-4">
                            <div className="text-center">
                              <div className="flex items-center justify-center space-x-1">
                                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {preview.stats.averageRating.toFixed(1)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                Average Rating
                              </div>
                            </div>
                          </Card>
                        </>
                      )}
                    </div>

                    {/* Tags */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Tags
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {template.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" size="sm">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Quick Stats */}
                    {preview && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                          What's Included
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {preview.categories.length}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Categories
                            </div>
                          </div>
                          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                              {preview.projectTypes.length}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Project Types
                            </div>
                          </div>
                          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                              {preview.activityTypes.length}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Activities
                            </div>
                          </div>
                          <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                              {preview.unitTypes.length}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Unit Types
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'categories' && preview && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Material Categories ({preview.categories.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {preview.categories.map((category) => (
                        <Card key={category.id} className="p-4">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                            {category.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                            {category.description}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {category.unitTypes.slice(0, 3).map((unit) => (
                              <Badge key={unit} variant="secondary" size="xs">
                                {unit}
                              </Badge>
                            ))}
                            {category.unitTypes.length > 3 && (
                              <Badge variant="secondary" size="xs">
                                +{category.unitTypes.length - 3}
                              </Badge>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'projects' && preview && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Project Types ({preview.projectTypes.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {preview.projectTypes.map((projectType) => (
                        <Card key={projectType.id} className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {projectType.name}
                            </h4>
                            <Badge 
                              className={complexityColors[projectType.complexity as keyof typeof complexityColors]} 
                              size="xs"
                            >
                              {projectType.complexity}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {projectType.description}
                          </p>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'activities' && preview && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Activity Types ({preview.activityTypes.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {preview.activityTypes.map((activity) => (
                        <Card key={activity.id} className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {activity.name}
                            </h4>
                            <Badge variant="info" size="xs">
                              ${activity.hourlyRate}/hr
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {activity.description}
                          </p>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'financial' && preview && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Financial Configuration
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Default Rates */}
                      <Card className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Default Rates
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Markup:</span>
                            <span className="font-medium">{preview.financialConfig.defaultMarkup}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Labor Rate:</span>
                            <span className="font-medium">${preview.financialConfig.laborRate}/hr</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Overhead:</span>
                            <span className="font-medium">{preview.financialConfig.overheadRate}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Profit Margin:</span>
                            <span className="font-medium">{preview.financialConfig.profitMargin}%</span>
                          </div>
                        </div>
                      </Card>

                      {/* Pricing Strategies */}
                      <Card className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Pricing Strategies
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {preview.financialConfig.pricingStrategies.map((strategy) => (
                            <Badge key={strategy} variant="info" size="sm">
                              {strategy}
                            </Badge>
                          ))}
                        </div>
                      </Card>

                      {/* Customer Segments */}
                      <Card className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Customer Segments
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {preview.financialConfig.customerSegments.map((segment) => (
                            <Badge key={segment} variant="secondary" size="sm">
                              {segment}
                            </Badge>
                          ))}
                        </div>
                      </Card>

                      {/* Seasonal Factors */}
                      <Card className="p-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Seasonal Factors
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {preview.financialConfig.seasonalFactors.map((factor) => (
                            <Badge key={factor} variant="warning" size="sm">
                              {factor}
                            </Badge>
                          ))}
                        </div>
                      </Card>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {preview && `Last updated: ${new Date(preview.stats.lastUpdated).toLocaleDateString()}`}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSelect}
              disabled={currentTemplateId === template.id}
            >
              {currentTemplateId === template.id ? (
                'Current Template'
              ) : (
                <>
                  {mode === 'switch' ? 'Switch to This Template' : 'Select This Template'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TemplatePreviewModal